import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { brandedQrDataUrl } from "./brandedQr";
import { SC, SCMonitor, SCPanel } from "./studioChrome";
import { loadPref, savePref } from "./prefs";
import {
  HOME_WORKSPACE_ID, layoutEqual, placeWidget, reconcileWorkspaces, removeWidget, seedWidgetDefaults,
  setWorkspaceLayout, validateWidgetPlacement, visibleWorkspaces, WorkspaceTabsEditor,
  type WidgetLayout, type WorkspaceDef, type WorkspaceWidgetDefaults,
} from "./studioWorkspaces";
import { WorkspaceCanvas, type WidgetDef } from "./WorkspaceCanvas";
import { Room, RoomEvent, Track } from "livekit-client";
import { useQuestionCue } from "./liveChat";
import { useProductionChat, ProductionChatPanel } from "./productionChat";
import WhiteboardCanvas from "./Whiteboard";
import {
  broadcastToken,
  createWhiteboardDevice,
  deckListPairings,
  deckPair,
  deckRevoke,
  deleteBackground,
  endBroadcast,
  ensureBroadcast,
  fetchBroadcastProgram,
  fetchCaveBackgrounds,
  muteBroadcast,
  pauseBroadcast,
  resumeBroadcast,
  setBroadcastProgram,
  stopBroadcast,
  track,
  unmuteBroadcast,
  uploadBackground,
  type Background,
  type Broadcast,
  type CaveEvent,
  type DeckAction,
  type DeckPairing,
  type DuelSlot,
  type ProgramState,
  type WhiteboardStroke,
} from "./api";
import { ChromaCompositor, loadImage, sampleVideoColor, chooseRawCameraSource, needsBackgroundRefetch, webglDiagnostics, gpuHealth, type Compositor, type BgLoadRecovery } from "./chromaKey";
import { MLCompositor } from "./mlSegment";
import ChromaControls from "./ChromaControls";
import { HOLD_DURATIONS } from "./holdDurations";
import { useDeckBridge } from "./deckBridge";
import { ConnectionBanner, selfSourceKey, useConnState } from "./liveConnection";
import { layoutSlots, slotSource } from "./programLayout";
import { useTalkbackListener } from "./talkback";
import { useTalentAudio } from "./talentAudio";
import { useDirectorOnAirAudio } from "./directorMic";
import { resolveBoardImageUrl, useBoardSlates } from "./boardSlates";
import { PLUGIN_DOWNLOAD_PATH } from "./streamdeckPlugin";
import {
  describeDeckError,
  hidSupported,
  openStreamDeck,
  KEY_COLORS,
  KEY_MUTE,
  KEY_PRIMARY,
  type Deck,
  type DeckControl,
} from "./streamdeck";

// Go Live surface (ADR 0011 — Phase 0). A legend/director opens an event's
// broadcast, previews their camera/mic, then publishes to the LiveKit room and
// sees themselves live. Camera pan/tilt/zoom is progressive enhancement
// (Chromium-only): feature-detected and hidden where unsupported.

/** The camera controls a browser might expose on the active video track. */
type PtzKey = "pan" | "tilt" | "zoom";
const PTZ_KEYS: PtzKey[] = ["pan", "tilt", "zoom"];
interface PtzRange {
  min: number;
  max: number;
  step?: number;
}

const PTZ_LABEL: Record<PtzKey, string> = { pan: "Pan", tilt: "Tilt", zoom: "Zoom" };

// The self-preview <video> filling a studioChrome SCMonitor stage (ADR 0087).
const monitorVideoStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  background: "#000",
};

// The Legend telestration canvas is width:100% at 16:9, so on a short laptop viewport a
// full-width board is taller than the screen (LSC-405). Cap its height to ~58vh
// (zoom-corrected like the console shell) by capping the width — it letterboxes centered
// on wide screens and fits within the viewport on a small laptop; WhiteboardCanvas's
// pointer mapping is canvas-rect-relative, so a narrower box stays exact.
const boardCanvasWrap: CSSProperties = {
  width: "100%",
  maxWidth: "calc(58vh / var(--ui-zoom, 1) * 16 / 9)",
  margin: "0 auto",
};

export interface GoLivePanelProps {
  caveId: string;
  event: CaveEvent;
  onClose?: () => void;
}

// Legend workspace profile (ADR 0126) — the Legend's own per-browser namespace, distinct
// from the Director's (different widget set). Reuses the shared workspace framework
// (ADR 0123–0125): tabs-as-data, dockable widget canvas, custom tabs.
interface LegendProfile {
  workspaces?: WorkspaceDef[];
}
const LEGEND_PROFILE_KEY = "legends.legend.profile";
const LEGEND_BUILTIN_WORKSPACES: WorkspaceDef[] = [
  { id: "console", name: "Console", builtin: true, hidden: false },
  { id: "background", name: "Background", builtin: true, hidden: false },
  { id: "board", name: "Board", builtin: true, hidden: false },
  { id: "questions", name: "Questions", builtin: true, hidden: false },
  { id: "deck", name: "Deck", builtin: true, hidden: false },
];
// One widget per tab for the (deliberately simpler) Legend surface; full-width defaults.
const LEGEND_WIDGETS: { id: string; title: string; w: number; h: number; minW: number; minH: number }[] = [
  { id: "console", title: "Console", w: 12, h: 14, minW: 4, minH: 6 },
  { id: "background", title: "Background", w: 12, h: 12, minW: 4, minH: 6 },
  { id: "board", title: "Board", w: 12, h: 14, minW: 4, minH: 6 },
  { id: "questions", title: "Questions", w: 12, h: 8, minW: 4, minH: 4 },
  { id: "deck", title: "Deck", w: 12, h: 10, minW: 4, minH: 4 },
  { id: "prodChat", title: "Production chat", w: 12, h: 8, minW: 4, minH: 4 },
];
const LEGEND_WIDGET_META: Record<string, { id: string; title: string; w: number; h: number; minW: number; minH: number }> =
  Object.fromEntries(LEGEND_WIDGETS.map((w) => [w.id, w]));
const LEGEND_WIDGET_IDS = LEGEND_WIDGETS.map((w) => w.id);
const lgi = (id: string): WidgetLayout => {
  const m = LEGEND_WIDGET_META[id];
  return { i: id, x: 0, y: 0, w: m.w, h: m.h, minW: m.minW, minH: m.minH };
};
const LEGEND_WIDGET_DEFAULTS: WorkspaceWidgetDefaults = {
  console: { widgets: ["console", "prodChat"], layout: [lgi("console"), { i: "prodChat", x: 0, y: 14, w: 12, h: 8, minW: 4, minH: 4 }] },
  background: { widgets: ["background"], layout: [lgi("background")] },
  board: { widgets: ["board"], layout: [lgi("board")] },
  questions: { widgets: ["questions"], layout: [lgi("questions")] },
  deck: { widgets: ["deck"], layout: [lgi("deck")] },
};

export default function GoLivePanel({ caveId, event, onClose }: GoLivePanelProps) {
  const [broadcast, setBroadcast] = useState<Broadcast | null>(null);
  // Whiteboard from Device (ADR 0095): a minted one-time code + its QR, so a touch
  // device can draw on the shared whiteboard as an auxiliary input.
  const [deviceUrl, setDeviceUrl] = useState<string | null>(null);
  const [deviceQr, setDeviceQr] = useState<string | null>(null);
  const [deviceBusy, setDeviceBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [mics, setMics] = useState<MediaDeviceInfo[]>([]);
  const [cameraId, setCameraId] = useState("");
  const [micId, setMicId] = useState("");

  const [ptzKeys, setPtzKeys] = useState<PtzKey[]>([]);
  const [ptzRanges, setPtzRanges] = useState<Partial<Record<PtzKey, PtzRange>>>({});
  const [ptzValues, setPtzValues] = useState<Partial<Record<PtzKey, number>>>({});

  const [live, setLive] = useState(false);
  const [connecting, setConnecting] = useState(false);
  // Private rehearsal (ADR 0244, LSC-507): a pre-live toggle; when on, go-live mints
  // the token with ?rehearsal=1 so this session is a private dry run.
  const [rehearsal, setRehearsal] = useState(false);
  const [identity, setIdentity] = useState<string | null>(null);
  const [roomName, setRoomName] = useState<string | null>(null);
  // The director's program state (ADR 0022) so the legend knows whether they're
  // on air, on standby, or off-air during an ad break.
  const [program, setProgram] = useState<ProgramState>({});

  const [micMuted, setMicMuted] = useState(false);
  // Timed auto-resume duration for the production hold (ADR 0055); "0" = manual.
  const [pauseSecondsStr, setPauseSecondsStr] = useState("0");
  const pauseSeconds = Number(pauseSecondsStr) || 0;
  const [ending, setEnding] = useState(false);
  // WebHID support is static for the environment — probe once.
  const [hidOk] = useState(hidSupported);
  const [deck, setDeck] = useState<Deck | null>(null);
  const [deckError, setDeckError] = useState<string | null>(null);
  // Stream Deck plugin pairing (ADR 0024) — the supported path (coexists with
  // the always-on Elgato app). WebHID above stays as a fallback.
  const [deckPairings, setDeckPairings] = useState<DeckPairing[]>([]);
  const [pairCode, setPairCode] = useState<string | null>(null);
  const [pairingStarted, setPairingStarted] = useState(false);

  const [conn, setConn] = useConnState();
  const [roomState, setRoomState] = useState<Room | null>(null); // re-render on connect; drives the talkback listener (ADR 0213)
  // Director talkback (ADR 0213): play the Director's private return-audio into a
  // hidden element + surface an on-air indicator when the Director is addressing this
  // Legend (or all talent). Keyed by the Legend's stable key (ADR 0042).
  const { talkbackActive, talkbackAudioRef } = useTalkbackListener(
    roomState,
    identity ? selfSourceKey(identity) ?? undefined : undefined,
  );
  // Talent conversation audio (LSC-530): the Legend hears every connected guest,
  // independent of Program. Fans/Director-talkback excluded (see talentAudio.tsx).
  const { containerRef: talentAudioRef } = useTalentAudio(roomState);
  // Director mic → On Air (LSC-633, ADR 0247): the Legend hears the Director's PUBLIC
  // on-air voice too — the same plane fans receive, separate from the private talkback.
  const { containerRef: directorMicRef } = useDirectorOnAirAudio(roomState);
  const questionCue = useQuestionCue(broadcast?.id ?? null, live);
  // Private Legend↔Director production chat (LSC-404) — polls whenever the Legend is in
  // the show (a broadcast exists), so they can coordinate before + during go-live.
  const prodChat = useProductionChat(broadcast?.id ?? null, !!broadcast);

  // Chroma-key backgrounds (ADR 0070): the cave's library + the compositor that
  // keys the Legend's camera over the selected background before publishing.
  const [backgrounds, setBackgrounds] = useState<Background[]>([]);
  const [bgBusy, setBgBusy] = useState(false);
  // Eyedropper: when armed, the preview shows the RAW camera and a click samples
  // the key color from it (ADR 0070).
  const [eyedropper, setEyedropper] = useState(false);
  // Bumped whenever the raw preview stream is (re)acquired, so the chroma
  // reconcile effect rebuilds the preview compositor against the fresh track.
  const [previewNonce, setPreviewNonce] = useState(0);
  // Bumped when the compositor's SOURCE track ends (a device change, or macOS/
  // LiveKit restarting the camera on focus loss) so the reconcile effect rebuilds
  // against the fresh track instead of compositing a dead one — the non-recovering
  // focus-freeze. Folded into the rebuild key alongside previewNonce.
  const [chromaSrcTick, setChromaSrcTick] = useState(0);

  // Workspace tab (ADR 0087) — the Legend console mirrors the Director's tabbed
  // studio shell with a reduced IA (one camera, no program/preview bus).
  const [tab, setTab] = useState("console");
  // Workspace framework (ADR 0123–0126): tabs-as-data + dockable widget canvas + custom
  // tabs, persisted in the Legend's own profile.
  const [workspaces, setWorkspaces] = useState<WorkspaceDef[]>(() =>
    validateWidgetPlacement(
      seedWidgetDefaults(
        reconcileWorkspaces(loadPref<LegendProfile>(LEGEND_PROFILE_KEY, {}).workspaces, LEGEND_BUILTIN_WORKSPACES),
        LEGEND_WIDGET_DEFAULTS,
      ),
      new Set(LEGEND_WIDGET_IDS),
    ),
  );
  const [tabsEditorOpen, setTabsEditorOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  useEffect(() => {
    savePref<LegendProfile>(LEGEND_PROFILE_KEY, { workspaces });
  }, [workspaces]);
  useEffect(() => {
    const active = workspaces.find((w) => w.id === tab);
    if (!active || active.hidden) setTab(HOME_WORKSPACE_ID);
  }, [workspaces, tab]);
  // ON-AIR elapsed clock for the header (mirrors the Director, ADR 0087).
  const [elapsedMs, setElapsedMs] = useState(0);
  const liveSinceRef = useRef<number | null>(null);
  useEffect(() => {
    if (!live) {
      liveSinceRef.current = null;
      setElapsedMs(0);
      return;
    }
    if (liveSinceRef.current == null) liveSinceRef.current = Date.now();
    const id = window.setInterval(() => {
      if (liveSinceRef.current != null) setElapsedMs(Date.now() - liveSinceRef.current);
    }, 1000);
    return () => window.clearInterval(id);
  }, [live]);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const roomRef = useRef<Room | null>(null);
  const mountedRef = useRef(true);
  // A deliberate Exit (ADR 0105) flags this so the room teardown's Disconnected
  // event is treated as expected (like the ended path) and never drives the
  // reconnect banner to a spurious "Connection lost".
  const exitingRef = useRef(false);
  const deckRef = useRef<Deck | null>(null);
  // Chroma compositor + the loaded background image + the raw camera track we
  // swapped out (so disabling can restore it on the live publication).
  const compositorRef = useRef<Compositor | null>(null);
  // Folds live/preview AND chroma/ml into one key (e.g. "live:ml") so a change to
  // either rebuilds the correct compositor from the correct source track.
  const compositorModeRef = useRef<string | null>(null);
  const bgImageRef = useRef<HTMLImageElement | null>(null);
  const bgImageIdRef = useRef<string>("");
  // De-dupes the chroma-fallback diagnostic so one failure (per reason+bg) emits once,
  // re-armed on a successful build so a later regression is still captured (LSC-623).
  const chromaDiagRef = useRef<string>("");
  // Dedup for the chroma_bg_recovered signal (LSC-623 follow-up): once per background id
  // the blob fallback rescues, so a re-reconcile of the same bg doesn't spam.
  const chromaRecoveredBgRef = useRef<string>("");
  const originalCamTrackRef = useRef<MediaStreamTrack | null>(null);
  // In-session camera switch (LSC-613): serialize swaps so two quick picks (or a
  // pick racing a chroma rebuild) can't overlap; drive a "Switching…" busy state.
  const switchingRef = useRef(false);
  const [switchingDevice, setSwitchingDevice] = useState(false);
  const tuneTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // An operator-facing alert shown in the Background panel when the chroma compositor
  // can't run and the raw green screen is going out (no WebGL / bg load / start fail),
  // set by the reconcile effect and cleared on a clean build (LSC-623 follow-up). It is
  // the loud counterpart to the silent `chroma_fallback` diagnostic — the Legend is the
  // one person who can fix the machine mid-show.
  const [chromaWarn, setChromaWarn] = useState<string | null>(null);
  // Pre-flight GPU posture, checked once: a software rasterizer / no WebGL means a
  // background won't work reliably on this machine — surface it BEFORE a live leak.
  const gpuAdvisory = useMemo(() => {
    const g = gpuHealth();
    if (g.health === "unavailable")
      return "This browser has no GPU access (hardware acceleration is off, or the GPU process has crashed), so a green-screen background can't run here — your raw camera goes on air. Turn on “Use graphics acceleration when available” in your browser settings, relaunch, and confirm chrome://gpu shows no disabled features.";
    if (g.health === "software")
      return "Your browser is running without GPU acceleration (software rendering), so green-screen backgrounds may stutter or drop out mid-show. Enable hardware acceleration in your browser settings and relaunch for a reliable background.";
    return null;
  }, []);

  const attachStream = (stream: MediaStream | null) => {
    if (!videoRef.current) return;
    try {
      videoRef.current.srcObject = stream;
    } catch {
      // jsdom / older browsers without srcObject — the preview is cosmetic.
    }
  };

  const stopPreview = useCallback(() => {
    const s = streamRef.current;
    streamRef.current = null;
    if (s) s.getTracks().forEach((t) => t.stop());
    attachStream(null);
  }, []);

  // Feature-detect pan/tilt/zoom on the active video track and surface sliders.
  const detectPtz = useCallback((stream: MediaStream) => {
    try {
      const supported = (navigator.mediaDevices.getSupportedConstraints?.() ?? {}) as Partial<
        Record<PtzKey, boolean>
      >;
      const track = stream.getVideoTracks()[0];
      if (!track || typeof track.getCapabilities !== "function") {
        setPtzKeys([]);
        return;
      }
      const caps = track.getCapabilities() as Partial<Record<PtzKey, PtzRange>>;
      const ranges: Partial<Record<PtzKey, PtzRange>> = {};
      const values: Partial<Record<PtzKey, number>> = {};
      const keys: PtzKey[] = [];
      for (const k of PTZ_KEYS) {
        const cap = caps[k];
        if (supported[k] && cap && typeof cap.min === "number" && typeof cap.max === "number") {
          ranges[k] = cap;
          values[k] = cap.min;
          keys.push(k);
        }
      }
      setPtzKeys(keys);
      setPtzRanges(ranges);
      setPtzValues(values);
    } catch {
      setPtzKeys([]);
    }
  }, []);

  // (Re)acquire a preview stream for the chosen devices while not live.
  const startPreview = useCallback(
    async (camId: string, micDevId: string) => {
      stopPreview();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: camId ? { deviceId: { exact: camId } } : true,
        audio: micDevId ? { deviceId: { exact: micDevId } } : true,
      });
      if (!mountedRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      attachStream(stream);
      detectPtz(stream);
      setPreviewNonce((n) => n + 1);
    },
    [detectPtz, stopPreview],
  );

  // On open: get-or-create the broadcast, then prime devices + preview.
  useEffect(() => {
    mountedRef.current = true;
    ensureBroadcast(caveId, event.id)
      .then((b) => {
        if (!mountedRef.current) return;
        setBroadcast(b);
        if (b.rehearsal) setRehearsal(true); // reflect an already-running private rehearsal
      })
      .catch((err: unknown) => mountedRef.current && setError(msg(err)));

    // Re-enumerate cameras/mics, preserving the current selection. Called on open and
    // on every `devicechange` (USB cam plug/unplug mid-session — LSC-613 acceptance #4).
    const refreshDevices = async (): Promise<boolean> => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (!mountedRef.current) return false;
        const cams = devices.filter((d) => d.kind === "videoinput");
        const audio = devices.filter((d) => d.kind === "audioinput");
        setCameras(cams);
        setMics(audio);
        setCameraId((prev) => prev || cams[0]?.deviceId || "");
        setMicId((prev) => prev || audio[0]?.deviceId || "");
        if (cams.length === 0) setError("No camera detected.");
        return cams.length > 0;
      } catch {
        /* enumerate can fail transiently during a device change — keep the prior list */
        return false;
      }
    };

    (async () => {
      let previewErr: unknown = null;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (!mountedRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        attachStream(stream);
        detectPtz(stream);
        setPreviewNonce((n) => n + 1);
      } catch (err: unknown) {
        // The cosmetic preview getUserMedia can fail — permission denied, or (Firefox) the
        // camera is already held by the live publication when the panel re-mounts: Firefox
        // can't open a camera twice. Don't error yet; enumeration below still works from the
        // existing permission grant, so the in-session camera switch stays usable (LSC-624).
        previewErr = err;
      }
      // Enumerate regardless of the preview outcome so the Camera/Mic selectors (pre-live AND
      // the LSC-613 in-session switch) populate on every browser — including Firefox, where the
      // preview gUM throws on a live re-mount and the old code (enumerate only on gUM success)
      // left the selector empty → "switching controls missing" (LSC-624).
      const hasCam = await refreshDevices();
      // Surface the preview failure UNLESS it's the benign "camera busy because we're already
      // live" case (Firefox NotReadableError/AbortError) that enumeration recovered from — a
      // real permission denial or missing device still shows (LSC-624).
      const benignBusy =
        previewErr instanceof DOMException &&
        (previewErr.name === "NotReadableError" || previewErr.name === "AbortError");
      if (previewErr && mountedRef.current && !(benignBusy && hasCam)) {
        setError(deviceErr(previewErr));
      }
    })();
    navigator.mediaDevices.addEventListener("devicechange", refreshDevices);

    return () => {
      mountedRef.current = false;
      navigator.mediaDevices.removeEventListener("devicechange", refreshDevices);
      if (tuneTimerRef.current) clearTimeout(tuneTimerRef.current);
      compositorRef.current?.stop();
      compositorRef.current = null;
      compositorModeRef.current = null;
      stopPreview();
      const room = roomRef.current;
      roomRef.current = null;
      if (room) void room.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caveId, event.id]);

  // Switch the active camera WITHOUT ending the broadcast (LSC-613). Serialized via
  // switchingRef so two quick picks (or a pick racing the chroma reconcile) can't
  // overlap and drop video — the LSC-329 replaceTrack-ordering race. Same
  // LiveKit room/participant/publication, audio + Program assignment + recording all
  // preserved; a brief video blip during the swap is acceptable.
  const switchCamera = async (id: string) => {
    const room = roomRef.current;
    if (!room || switchingRef.current) return;
    switchingRef.current = true;
    setSwitchingDevice(true);
    setError(null);
    const comp = compositorRef.current;
    try {
      if (comp) {
        // Chroma/ML active: acquire the new device's RAW track and feed it to the
        // compositor in place (setSource). The published composited canvas track is
        // untouched — no replaceTrack, no Program drop. Re-arm the ended→rebuild
        // listener on the new raw and stop the old one.
        const stream = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: id } } });
        const newRaw = stream.getVideoTracks()[0];
        if (!newRaw) throw new Error("no camera track for the selected device");
        const old = originalCamTrackRef.current;
        originalCamTrackRef.current = newRaw;
        newRaw.addEventListener("ended", () => setChromaSrcTick((n) => n + 1), { once: true });
        comp.setSource(newRaw);
        if (old && old !== newRaw) old.stop();
        detectPtz(new MediaStream([newRaw]));
      } else {
        // Raw publish: swap the camera on the existing publication in place (same
        // SSRC/publication, no renegotiation). NOT used in chroma mode — it would
        // restart the publication track and clobber the canvas composite.
        await room.switchActiveDevice("videoinput", id, true);
        const vt = room.localParticipant.getTrackPublication(Track.Source.Camera)?.videoTrack;
        if (vt) {
          if (videoRef.current) vt.attach(videoRef.current);
          if (vt.mediaStreamTrack) detectPtz(new MediaStream([vt.mediaStreamTrack]));
        }
      }
    } catch (err: unknown) {
      // Switch failed — the old camera keeps running (switchActiveDevice is atomic;
      // the compositor keeps its prior source). Surface it; the operator can retry.
      setError(deviceErr(err));
    } finally {
      switchingRef.current = false;
      setSwitchingDevice(false);
    }
  };

  const switchMic = async (id: string) => {
    const room = roomRef.current;
    if (!room) return;
    try {
      await room.switchActiveDevice("audioinput", id, true); // works for raw + chroma (mic is never composited)
    } catch (err: unknown) {
      setError(deviceErr(err));
    }
  };

  const onPickCamera = (id: string) => {
    setCameraId(id);
    if (live) void switchCamera(id);
    else startPreview(id, micId).catch((err: unknown) => setError(deviceErr(err)));
  };
  const onPickMic = (id: string) => {
    setMicId(id);
    if (live) void switchMic(id);
    else startPreview(cameraId, id).catch((err: unknown) => setError(deviceErr(err)));
  };

  const onPtzChange = (key: PtzKey, value: number) => {
    setPtzValues((v) => ({ ...v, [key]: value }));
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      const constraint: Record<string, number> = { [key]: value };
      void track.applyConstraints({ advanced: [constraint] } as MediaTrackConstraints);
    } catch {
      // PTZ not applicable — never crash the surface.
    }
  };

  const goLive = async () => {
    if (!broadcast || connecting) return;
    setError(null);
    setConnecting(true);
    setConn("live");
    try {
      const t = await broadcastToken(broadcast.id, { rehearsal });
      const room = new Room();
      roomRef.current = room;
      room.on(RoomEvent.Disconnected, () => {
        // A deliberate Exit (ADR 0105) or our own teardown (which nulls roomRef
        // first) is expected — only a genuine, unexpected drop shows the banner.
        if (mountedRef.current && roomRef.current === room && !exitingRef.current) {
          roomRef.current = null;
          setLive(false);
          // A hard drop (not our own teardown, which nulls roomRef first) — flag
          // it so the legend gets a clear "Connection lost" + rejoin (ADR 0042).
          setConn("lost");
        }
      });
      // Reconnect messaging (ADR 0042).
      room.on(RoomEvent.Reconnecting, () => {
        if (mountedRef.current) setConn("reconnecting");
      });
      room.on(RoomEvent.Reconnected, () => {
        if (mountedRef.current) setConn("reconnected");
      });
      // Track the director's program state so the legend sees on-air / off-air.
      room.on(RoomEvent.DataReceived, (payload: Uint8Array) => {
        try {
          const parsed = JSON.parse(new TextDecoder().decode(payload)) as { type?: string; state?: ProgramState };
          if (parsed.type === "program" && parsed.state && mountedRef.current) setProgram(parsed.state);
        } catch {
          // ignore non-program / malformed data messages
        }
      });
      await room.connect(t.url, t.token);
      // Seed from the persisted program (the director may already be mid-show).
      fetchBroadcastProgram(broadcast.id)
        .then((p) => mountedRef.current && setProgram(p))
        .catch(() => undefined);
      // Release the raw preview so LiveKit can claim the same camera device.
      stopPreview();
      await room.localParticipant.setCameraEnabled(true, cameraId ? { deviceId: cameraId } : undefined);
      await room.localParticipant.setMicrophoneEnabled(true, micId ? { deviceId: micId } : undefined);
      const pub = room.localParticipant.getTrackPublication(Track.Source.Camera);
      const videoTrack = pub?.videoTrack;
      if (videoRef.current && videoTrack) videoTrack.attach(videoRef.current);
      if (!mountedRef.current) return;
      setIdentity(t.identity);
      setRoomName(t.room);
      setRoomState(room); // enables the live chat / viewer questions channel
      setLive(true);
    } catch (err: unknown) {
      const room = roomRef.current;
      roomRef.current = null;
      if (room) void room.disconnect();
      if (mountedRef.current) {
        setError(msg(err));
        // Fall back to the pre-live preview so the operator can retry.
        startPreview(cameraId, micId).catch(() => undefined);
      }
    } finally {
      if (mountedRef.current) setConnecting(false);
    }
  };

  // teardown tears down only the LOCAL publish (release the room + camera, fall
  // back to the pre-live preview). It does NOT touch server state — used by the
  // resume-safe stop/end handlers and unmount.
  const teardown = () => {
    const room = roomRef.current;
    roomRef.current = null;
    // Stop compositing; the published track vanishes with the room, so no revert.
    compositorRef.current?.stop();
    compositorRef.current = null;
    compositorModeRef.current = null;
    originalCamTrackRef.current = null;
    setLive(false);
    setIdentity(null);
    setRoomName(null);
    setRoomState(null);
    setProgram({});
    if (room) void room.disconnect();
    startPreview(cameraId, micId).catch((err: unknown) => setError(deviceErr(err)));
  };

  // Exit (ADR 0092/0105): leave the console WITHOUT ending the show, returning to
  // the prior view. This is a DELIBERATE teardown — flag it (exitingRef) and clear
  // any in-flight reconnect state BEFORE releasing the LiveKit room, so the room's
  // Disconnected event fired during teardown never flashes a spurious "Connection
  // lost" banner (distinct from End, which suppresses via program.ended). A genuine
  // mid-show drop — where the flag is NOT set — still surfaces the banner (ADR 0042).
  const exitShow = () => {
    if (!onClose) return;
    exitingRef.current = true;
    setConn("live"); // hide any reconnecting/lost banner on the way out
    const room = roomRef.current;
    roomRef.current = null;
    if (room) void room.disconnect();
    onClose();
  };

  // Stop into a recoverable standby (ADR 0039): mark it stopped server-side and
  // relay standby to viewers, THEN tear down the local publish. Distinct from End
  // (terminal). This also fixes the phantom-live bug — a local-only stop left the
  // server (and viewers) believing the legend was still live.
  const stopShow = async () => {
    if (!broadcast) {
      teardown();
      return;
    }
    // Relay the STANDBY intent FIRST — before the server round-trip and before the
    // local teardown drops our publish — so a fan never sees the involuntary
    // "Signal lost" slate for a deliberate standby (ADR 0062). The persisted state
    // + the fan-side grace cover the residual ordering race.
    const room = roomRef.current;
    if (room) {
      room.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify({ type: "program", state: { standby: true } })),
        { reliable: true },
      );
    }
    try {
      await stopBroadcast(broadcast.id);
    } catch (err: unknown) {
      if (mountedRef.current) setError(msg(err));
    }
    teardown();
  };

  // End the whole broadcast (ADR 0025): either the legend or the director may
  // call the show. Mark it ended server-side, relay the end state to viewers,
  // then tear down publishing. Terminal — unlike Stop, there is no resume.
  const endShow = async () => {
    if (!broadcast) return;
    setEnding(true);
    // Relay the END intent FIRST — before the server round-trip and before the
    // local teardown drops our publish — so a fan never sees the involuntary
    // "Signal lost" slate for a deliberate end (ADR 0062). The persisted end state
    // + the fan-side grace cover the residual ordering race.
    const room = roomRef.current;
    if (room) {
      room.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify({ type: "program", state: { ended: true } })),
        { reliable: true },
      );
    }
    try {
      await endBroadcast(broadcast.id);
      teardown();
      if (mountedRef.current) setProgram({ ended: true });
    } catch (err: unknown) {
      if (mountedRef.current) setError(msg(err));
    } finally {
      if (mountedRef.current) setEnding(false);
    }
  };

  // Relay a program update the SAME way the Director does (ADR 0045): a LiveKit
  // data message for instant fan-out to connected viewers + the API for
  // persistence (late-joiners and the Director's shared board). The Legend is a
  // publisher with the data channel and holds the same production gate on
  // PUT /program, so this reuses the existing opaque relay — no backend change.
  const relayProgram = useCallback(
    async (next: ProgramState) => {
      // Carry an explicit `live` (false until the Director's TAKE, ADR 0149) so a Legend
      // relay (chroma/board) sent during warmup — pre-TAKE — doesn't drop the field and
      // re-arm a fan client's frame heuristic (LSC-443/440). A Director-set `live:true`
      // (relayed to the Legend) is preserved; only an undefined field is defaulted.
      if (next.live === undefined) next = { ...next, live: false };
      setProgram(next);
      const room = roomRef.current;
      try {
        if (room) {
          room.localParticipant.publishData(
            new TextEncoder().encode(JSON.stringify({ type: "program", state: next })),
            { reliable: true },
          );
        }
        if (broadcast) await setBroadcastProgram(broadcast.id, next);
      } catch (err: unknown) {
        if (mountedRef.current) setError(msg(err));
      }
    },
    [broadcast],
  );

  // Production hold (ADR 0055) — the Legend mutes program audio / pauses program
  // video for fans (the Director can do the same on their behalf). Each toggle
  // does BOTH: an instant data relay for connected viewers AND the authoritative
  // server endpoint (a real SFU track mute + persistence). The Legend's own camera
  // preview keeps running (this is the FAN-facing hold). Pause offers a timed
  // auto-resume (pauseSeconds; 0 = until I resume).
  const relayHoldLocal = (next: ProgramState) => {
    setProgram(next);
    const room = roomRef.current;
    if (room) {
      room.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify({ type: "program", state: next })),
        { reliable: true },
      );
    }
  };
  const toggleAudioMuted = () => {
    if (!broadcast) return;
    const nextOn = !program.audioMuted;
    const next: ProgramState = { ...program, audioMuted: nextOn };
    if (!nextOn && !next.videoPaused) delete next.resumeAt;
    relayHoldLocal(next);
    (nextOn ? muteBroadcast(broadcast.id) : unmuteBroadcast(broadcast.id)).catch((err: unknown) => {
      if (mountedRef.current) setError(msg(err));
    });
  };
  const toggleVideoPaused = () => {
    if (!broadcast) return;
    const nextOn = !program.videoPaused;
    const next: ProgramState = { ...program, videoPaused: nextOn };
    if (nextOn && pauseSeconds > 0) next.resumeAt = Date.now() + pauseSeconds * 1000;
    if (!nextOn && !next.audioMuted) delete next.resumeAt;
    relayHoldLocal(next);
    (nextOn ? pauseBroadcast(broadcast.id, pauseSeconds || undefined) : resumeBroadcast(broadcast.id)).catch(
      (err: unknown) => {
        if (mountedRef.current) setError(msg(err));
      },
    );
  };
  // Deck "show / hide board" (ADR 0096): the Legend relays the shared whiteboard
  // overlay on or off program — a quick way to reveal the board they're drawing.
  const toggleBoardOverlay = () => {
    if (!broadcast) return;
    if (program.overlay?.kind === "whiteboard") {
      void relayProgram({ ...program, overlay: { kind: "none" } });
    } else {
      void relayProgram({ ...program, overlay: { kind: "whiteboard", strokes: [], board: "blank" } });
    }
  };

  // ── Chroma-key background (ADR 0070) ────────────────────────────────────────
  // The Legend keys their green-screen camera over a selected background BEFORE
  // publishing, so the composited result is what reaches Program/Fans. The
  // selection rides program.chroma (opaque) so the Director can override it.
  const chroma = program.chroma;

  const refreshBackgrounds = useCallback(() => {
    fetchCaveBackgrounds(caveId)
      .then((bs) => mountedRef.current && setBackgrounds(bs))
      .catch(() => undefined);
  }, [caveId]);
  useEffect(() => {
    refreshBackgrounds();
  }, [refreshBackgrounds]);

  // Re-fetch the background library when an inbound program.chroma references a
  // backgroundId we don't have (LSC-339). On a fresh Cave the Director can select or
  // upload a background the Legend fetched-empty at mount; without this the Legend
  // can't resolve the id, so a Director-initiated first background never composites
  // until the Legend seeds one locally. Guarded by the last id tried (one refetch
  // per unknown id — no loop if it's genuinely gone).
  const bgRefetchedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (needsBackgroundRefetch(chroma?.backgroundId, backgrounds.map((b) => b.id), bgRefetchedForRef.current)) {
      bgRefetchedForRef.current = chroma?.backgroundId ?? null;
      refreshBackgrounds();
    } else if (chroma?.backgroundId && backgrounds.some((b) => b.id === chroma.backgroundId)) {
      bgRefetchedForRef.current = null; // resolved — allow a future refetch if it changes again
    }
  }, [chroma?.backgroundId, backgrounds, refreshBackgrounds]);

  // Tear down the compositor and restore the raw camera — on the live publication
  // (replaceTrack back) or the preview element. Safe when nothing is composited.
  const teardownChroma = useCallback(() => {
    const comp = compositorRef.current;
    const mode = compositorModeRef.current;
    compositorRef.current = null;
    compositorModeRef.current = null;
    if (!comp) return;
    try {
      if (mode?.startsWith("live") && originalCamTrackRef.current) {
        const vt = roomRef.current?.localParticipant.getTrackPublication(Track.Source.Camera)?.videoTrack;
        void vt?.replaceTrack(originalCamTrackRef.current);
        if (vt && videoRef.current) vt.attach(videoRef.current);
      } else if (mode?.startsWith("preview") && videoRef.current) {
        videoRef.current.srcObject = streamRef.current;
      }
    } catch {
      /* ignore */
    }
    originalCamTrackRef.current = null;
    comp.stop();
  }, []);

  // Reconcile the compositor with the desired chroma state. The composited canvas
  // track REPLACES the published camera track (live) or drives the preview
  // (pre-live). Best-effort: if WebGL is unavailable or the image won't load, we
  // keep the raw camera — chroma never blocks going live.
  useEffect(() => {
    const cmode = chroma?.mode ?? "chroma";
    const supported = cmode === "ml" ? MLCompositor.supported() : ChromaCompositor.supported();
    const wantsChroma = !!chroma?.enabled; // operator intent, independent of machine support
    const enabled = wantsChroma && supported;
    const bg = backgrounds.find((b) => b.id === chroma?.backgroundId) || null;
    // Mode key folds live/preview AND chroma/ml — a change to either rebuilds. It
    // ALSO folds previewNonce + chromaSrcTick so a camera device change or a source
    // track that ended (macOS/LiveKit restarting the camera on focus loss) forces a
    // rebuild against the FRESH track — otherwise the compositor keeps feeding its
    // hidden <video> a dead track and the composited output freezes permanently.
    const desiredMode: string | null =
      enabled && bg ? `${live ? "live" : "preview"}:${cmode}:${previewNonce}:${chromaSrcTick}` : null;
    const params = {
      color: chroma?.color,
      similarity: chroma?.similarity,
      smoothness: chroma?.smoothness,
      spill: chroma?.spill,
      fit: chroma?.fit,
    };
    let cancelled = false;

    // Emit ONE diagnostic when chroma falls back to the raw camera (the green screen
    // leaks on air): the reason + GPU renderer + MAX_TEXTURE_SIZE + bg dims, so a future
    // incident is explainable WITHOUT the operator's machine (LSC-623).
    const emitFallback = (reason: string, bgEl: HTMLImageElement | null, err?: unknown) => {
      const key = reason + ":" + (bg?.id ?? "");
      if (chromaDiagRef.current === key) return;
      chromaDiagRef.current = key;
      const d = webglDiagnostics();
      track("chroma_fallback", {
        caveId,
        cavecastId: broadcast?.id,
        props: {
          reason,
          mode: chroma?.mode ?? "chroma",
          bgId: bg?.id ?? "",
          // bgUrl (the resolved /uploads path) + the thrown error reveal whether the load
          // died at fetch (status in the message) or decode, and whether it was cross-origin
          // — the detail that was missing when this burned a live show (LSC-623).
          bgUrl: bg?.imageUrl ?? "",
          bgW: bgEl?.naturalWidth ?? 0,
          bgH: bgEl?.naturalHeight ?? 0,
          maxTextureSize: d.maxTextureSize,
          renderer: d.renderer,
          live,
          ua: navigator.userAgent,
          err: err != null ? String(err) : undefined,
        },
      });
    };

    // Emit ONE diagnostic when the resilient loader (LSC-623) had to RESCUE a background:
    // the crossOrigin `<img>` failed and the same-origin blob fallback succeeded. This is
    // the recovery counterpart to emitFallback — it means CORS/cache (not GPU) was the
    // latent cause AND the /uploads ACAO header isn't reaching this operator (e.g. a
    // caching proxy serving a stale no-CORS entry). Without it, a rescued load is silent
    // and the mechanism stays unconfirmed. Deduped per background id.
    const emitRecovered = (r: BgLoadRecovery, bgEl: HTMLImageElement | null) => {
      const id = bg?.id ?? "";
      if (chromaRecoveredBgRef.current === id) return;
      chromaRecoveredBgRef.current = id;
      const d = webglDiagnostics();
      track("chroma_bg_recovered", {
        caveId,
        cavecastId: broadcast?.id,
        props: {
          reason: "blob_fallback",
          mode: chroma?.mode ?? "chroma",
          bgId: id,
          bgUrl: bg?.imageUrl ?? "",
          bgW: bgEl?.naturalWidth ?? 0,
          bgH: bgEl?.naturalHeight ?? 0,
          maxTextureSize: d.maxTextureSize,
          renderer: d.renderer,
          // The /uploads response the browser actually got (LSC-623): whether the server
          // sent ACAO, how the browser classified it (basic/cors/opaque), the status, and
          // whether the bg was cross-origin vs the cave host. `acao:null` + `respType:opaque`
          // ⇒ CORS/cache was the cause and the ACAO header isn't reaching this operator.
          acao: r.acao,
          respType: r.respType,
          status: r.status,
          contentType: r.contentType,
          crossOrigin: r.crossOrigin,
          origin: r.origin,
          live,
          ua: navigator.userAgent,
        },
      });
    };

    // The operator turned a background ON but this machine can't run the compositor
    // (no WebGL — the GPU process is dead / WebGL blocklisted, Doug's chrome://gpu
    // "Disabled Features: all"). The async build path below never runs, so WITHOUT
    // this the raw green screen leaks on air with NO fallback diagnostic and NO
    // operator signal — the exact silent failure that burned WS Game 3 (LSC-623).
    // Emit the diagnostic + raise the loud warning here instead.
    if (wantsChroma && !supported) {
      emitFallback("webgl_unsupported", null);
      setChromaWarn(
        "A background is turned on but can’t run on this machine, so your raw camera (green screen) is on air. Turn on hardware acceleration in your browser and relaunch — chrome://gpu should show no disabled features. (The background is still saved; it will key once the GPU is available.)",
      );
      return () => {
        cancelled = true;
      };
    }
    // Chroma off (or no background selected): clear any prior leak warning. A running
    // compositor is torn down below; the pre-flight GPU advisory is separate.
    if (!wantsChroma) setChromaWarn(null);

    void (async () => {
      if (bg && (bgImageIdRef.current !== bg.id || !bgImageRef.current)) {
        let rec: BgLoadRecovery | null = null;
        try {
          const img = await loadImage(bg.imageUrl, (r) => {
            rec = r;
          });
          if (cancelled) return;
          bgImageRef.current = img;
          bgImageIdRef.current = bg.id;
          if (rec) emitRecovered(rec, img);
        } catch (e) {
          emitFallback("bg_load_failed", null, e);
          setChromaWarn(
            "Your background image couldn’t load, so your raw camera (green screen) is on air. Re-select the background; if it keeps failing, check your network/VPN or try a different background.",
          );
          return;
        }
      }
      if (cancelled) return;

      if (compositorModeRef.current !== desiredMode) {
        // Capture the genuine raw camera BEFORE teardown. On a mode switch the prior
        // compositor's source (still live — comp.stop() only stops its own canvas
        // track) is the real camera; the publication's mediaStreamTrack, read AFTER
        // teardown's fire-and-forget replaceTrack(→raw), still transiently reports the
        // just-stopped OLD composited track — sourcing the new compositor from that
        // dead track froze video for Legend/Director/Fan (LSC-329). chooseRawCameraSource
        // prefers the live prior raw; a fresh build (no prior compositor) falls back to
        // the publication's current track (the genuine raw — nothing replaced it yet).
        const priorRaw = originalCamTrackRef.current;
        teardownChroma();
        if (!desiredMode) return;
        const comp: Compositor = cmode === "ml" ? new MLCompositor() : new ChromaCompositor();
        comp.setParams(params);
        comp.setBackground(bgImageRef.current);
        try {
          if (live) {
            const vt = roomRef.current?.localParticipant.getTrackPublication(Track.Source.Camera)?.videoTrack;
            const raw = chooseRawCameraSource(priorRaw, vt?.mediaStreamTrack);
            if (!vt || !raw) return void comp.stop();
            originalCamTrackRef.current = raw;
            raw.addEventListener("ended", () => setChromaSrcTick((n) => n + 1), { once: true });
            const out = await comp.start(raw);
            if (cancelled) return void comp.stop();
            await vt.replaceTrack(out.getVideoTracks()[0]);
            if (videoRef.current) videoRef.current.srcObject = out;
          } else {
            const raw = streamRef.current?.getVideoTracks()[0];
            if (!raw) return void comp.stop();
            raw.addEventListener("ended", () => setChromaSrcTick((n) => n + 1), { once: true });
            const out = await comp.start(raw);
            if (cancelled) return void comp.stop();
            if (videoRef.current) videoRef.current.srcObject = out;
          }
          compositorRef.current = comp;
          compositorModeRef.current = desiredMode;
          chromaDiagRef.current = ""; // built cleanly — re-arm the fallback diagnostic
          setChromaWarn(null); // compositor is running — clear any leak warning
        } catch {
          emitFallback("compositor_start_failed", bgImageRef.current);
          setChromaWarn(
            "The background compositor failed to start on this machine, so your raw camera (green screen) is on air. Enable hardware acceleration in your browser and relaunch — chrome://gpu should show no disabled features.",
          );
          comp.stop();
          teardownChroma();
        }
      } else if (desiredMode) {
        compositorRef.current?.setParams(params);
        compositorRef.current?.setBackground(bgImageRef.current);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, previewNonce, chromaSrcTick, backgrounds, chroma?.enabled, chroma?.mode, chroma?.backgroundId, chroma?.color, chroma?.similarity, chroma?.smoothness, chroma?.spill, chroma?.fit]);

  // Set the chroma selection on program state as the LEGEND (rides relayProgram —
  // opaque data relay + persistence). null clears it.
  const setLegendChroma = useCallback(
    (patch: Partial<NonNullable<ProgramState["chroma"]>> | null) => {
      const next: ProgramState = { ...program };
      if (patch === null) delete next.chroma;
      else next.chroma = { ...(program.chroma ?? {}), ...patch, by: "legend" };
      void relayProgram(next);
    },
    [program, relayProgram],
  );

  // Key-tuning / fit changes update the preview INSTANTLY (local program state →
  // the reconcile effect pushes new params into the running compositor) but the
  // network relay+persist is DEBOUNCED so a slider drag doesn't spam PUT /program.
  const tuneChroma = useCallback(
    (patch: Partial<NonNullable<ProgramState["chroma"]>>) => {
      const next: ProgramState = { ...program, chroma: { ...(program.chroma ?? {}), ...patch, by: "legend" } };
      setProgram(next);
      if (tuneTimerRef.current) clearTimeout(tuneTimerRef.current);
      tuneTimerRef.current = setTimeout(() => void relayProgram(next), 400);
    },
    [program, relayProgram],
  );

  // The RAW (un-composited) camera track — the eyedropper samples from this so a
  // pick works whether or not chroma is currently compositing.
  const rawCameraTrack = useCallback((): MediaStreamTrack | null => {
    return (
      originalCamTrackRef.current ||
      roomRef.current?.localParticipant.getTrackPublication(Track.Source.Camera)?.videoTrack?.mediaStreamTrack ||
      streamRef.current?.getVideoTracks()[0] ||
      null
    );
  }, []);

  // While the eyedropper is armed, show the RAW camera in the preview (so the
  // operator can see + click the key even with compositing on); restore the
  // composited/preview view when disarmed.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (eyedropper) {
      const raw = rawCameraTrack();
      if (raw) {
        try {
          el.srcObject = new MediaStream([raw]);
        } catch {
          /* ignore */
        }
      }
      return;
    }
    const comp = compositorRef.current?.stream;
    if (comp) {
      try {
        el.srcObject = comp;
        return;
      } catch {
        /* fall through */
      }
    }
    const liveTrack = roomRef.current?.localParticipant.getTrackPublication(Track.Source.Camera)?.videoTrack;
    if (liveTrack) {
      try {
        liveTrack.attach(el);
        return;
      } catch {
        /* fall through */
      }
    }
    attachStream(streamRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eyedropper, rawCameraTrack]);

  // Sample the key color from the raw camera at the click point (objectFit:cover).
  const sampleKeyColor = (el: HTMLVideoElement, clientX: number, clientY: number) => {
    const hex = sampleVideoColor(el, clientX, clientY);
    if (hex) tuneChroma({ color: hex, enabled: true });
    setEyedropper(false);
  };

  const selectBackground = (id: string) => setLegendChroma({ backgroundId: id, enabled: true });
  const toggleUseBackground = () => {
    const first = chroma?.backgroundId || backgrounds[0]?.id;
    if (!chroma?.enabled && !first) return;
    setLegendChroma({ enabled: !chroma?.enabled, backgroundId: chroma?.backgroundId || first });
  };
  const onUploadBackgroundFile = async (file: File) => {
    setBgBusy(true);
    try {
      const bg = await uploadBackground(caveId, file);
      if (!mountedRef.current) return;
      await fetchCaveBackgrounds(caveId)
        .then((bs) => mountedRef.current && setBackgrounds(bs))
        .catch(() => undefined);
      setLegendChroma({ backgroundId: bg.id, enabled: true });
    } catch (err: unknown) {
      if (mountedRef.current) setError(msg(err));
    } finally {
      if (mountedRef.current) setBgBusy(false);
    }
  };
  const onRemoveBackground = async (id: string) => {
    try {
      await deleteBackground(caveId, id);
      if (!mountedRef.current) return;
      if (chroma?.backgroundId === id) setLegendChroma(null);
      refreshBackgrounds();
    } catch (err: unknown) {
      if (mountedRef.current) setError(msg(err));
    }
  };

  // Which dueling slot (ADR 0054) this publisher is assigned to, if any. In the
  // `dueling` layout each of the two heads draws only its OWN board, keyed by
  // slot ("a"/"b"). A slot is bound 1:1 to a publisher's STABLE key (ADR 0042):
  // `source` = slot A, `sourceB` = slot B — so slot-keying IS owner-keying, and
  // it survives a reconnect that mints a fresh identity. A publisher on neither
  // slot has no board.
  const selfKey = selfSourceKey(identity);
  const myDuelSlot: DuelSlot | null =
    program.layout === "dueling" && selfKey
      ? program.source === selfKey
        ? "a"
        : program.sourceB === selfKey
          ? "b"
          : null
      : null;
  const duelBoard = myDuelSlot ? program.boards?.[myDuelSlot] : undefined;
  const duelling = live && !!myDuelSlot && !program.standby && !program.ended;

  // The board-slate catalog (ADR 0058): the Director may have set the board to an
  // admin-uploaded image slate, which the Legend draws on the same as a vector board.
  const boardSlates = useBoardSlates();

  // The Legend telestrates on the SHARED board when live and the Director has
  // staged the whiteboard overlay (they own the board choice; the Legend owns the
  // drawing — ADR 0045). The shared board is single/`dual-board`; the `dueling`
  // per-slot boards below are the separate multi-head path (ADR 0054).
  const wb = program.overlay?.kind === "whiteboard" ? program.overlay : null;
  const telestrating =
    live && !!wb && !program.standby && !program.ended && program.layout !== "dueling";
  // Draw / clear append to the strokes the Legend last saw (seeded from program
  // state) so the Director's and the Legend's marks share one board.
  const drawStrokes = (next: WhiteboardStroke[]) => {
    if (!wb) return;
    void relayProgram({ ...program, overlay: { ...wb, kind: "whiteboard", strokes: next } });
  };
  const clearBoard = () => {
    if (!wb) return;
    void relayProgram({ ...program, overlay: { ...wb, kind: "whiteboard", strokes: [] } });
  };

  // Whiteboard from Device (ADR 0095): mint a one-time code + QR so a touch device
  // (iPad/tablet/phone) can draw on the SHARED whiteboard as an auxiliary input —
  // the laptop canvas above stays fully usable; the device is additive.
  const openWhiteboardDevice = () => {
    if (!broadcast || deviceBusy) return;
    setDeviceBusy(true);
    createWhiteboardDevice(broadcast.id)
      .then(async (d) => {
        if (!mountedRef.current) return;
        setDeviceUrl(d.joinUrl);
        try {
          setDeviceQr(await brandedQrDataUrl(d.joinUrl, { size: 240 }));
        } catch {
          setDeviceQr(null);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (mountedRef.current) setDeviceBusy(false);
      });
  };

  // Draw / clear this publisher's OWN dueling board (ADR 0054): merge into the
  // per-slot `boards` map (keyed by slot) so the other slot's board is untouched,
  // then relay it the SAME opaque data+PUT way as the shared board. The Director
  // set the slot's template; the publisher owns its strokes.
  const drawDuelStrokes = (next: WhiteboardStroke[]) => {
    if (!myDuelSlot) return;
    const boards = { ...(program.boards ?? {}) };
    boards[myDuelSlot] = { ...(boards[myDuelSlot] ?? {}), strokes: next };
    void relayProgram({ ...program, boards });
  };
  const clearDuelBoard = () => {
    if (!myDuelSlot) return;
    const boards = { ...(program.boards ?? {}) };
    boards[myDuelSlot] = { ...(boards[myDuelSlot] ?? {}), strokes: [] };
    void relayProgram({ ...program, boards });
  };

  // Toggling full-screen remounts the <video>; re-attach the preview/live stream.
  useEffect(() => {
    const live = roomRef.current?.localParticipant.getTrackPublication(Track.Source.Camera)?.videoTrack;
    if (live && videoRef.current) {
      try {
        live.attach(videoRef.current);
        return;
      } catch {
        // fall through to the local preview stream
      }
    }
    attachStream(streamRef.current);
    // Runs once on mount: the panel is full-screen-only now (ADR 0074), so the
    // preview <video> mounts a single time — no layout toggle to re-attach for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mic mute toggles the LiveKit local participant while live, or the preview
  // audio track before going live. The visible state + deck key follow it.
  const toggleMute = useCallback(() => {
    setMicMuted((muted) => {
      const next = !muted;
      try {
        const room = roomRef.current;
        if (room) {
          void room.localParticipant.setMicrophoneEnabled(!next);
        } else {
          const track = streamRef.current?.getAudioTracks()[0];
          if (track) track.enabled = !next;
        }
      } catch {
        // Never let a mute toggle crash the surface.
      }
      return next;
    });
  }, []);

  // Latest live/mute state + actions for the single 'down' handler to dispatch
  // against, so the physical keymap tracks the panel without re-subscribing.
  const stateRef = useRef({ live, micMuted });
  stateRef.current = { live, micMuted };
  const actionRef = useRef({ goLive, stop: stopShow, toggleMute, toggleVideoPaused, toggleBoardOverlay, ackQuestion: () => {} });
  actionRef.current = {
    goLive, stop: stopShow, toggleMute, toggleVideoPaused, toggleBoardOverlay,
    ackQuestion: () => { questionCue.ack().catch(() => undefined); },
  };

  // A deck key press (from either the plugin bridge or WebHID) toggles based on
  // the tab's own current state — the tab is the source of truth (ADR 0024/0096).
  const handleDeckAction = useCallback((action: DeckAction) => {
    const a = actionRef.current;
    switch (action) {
      case "live":
        if (stateRef.current.live) void a.stop();
        else void a.goLive();
        break;
      case "mute":
        a.toggleMute();
        break;
      case "hold":
        a.toggleVideoPaused();
        break;
      case "board":
        a.toggleBoardOverlay();
        break;
      case "question":
        a.ackQuestion();
        break;
      default:
        break;
    }
  }, []);

  // Load the operator's existing deck pairings so the bridge runs for returning
  // users (and the UI shows "paired").
  const loadPairings = useCallback(() => {
    deckListPairings()
      .then((r) => mountedRef.current && setDeckPairings(r.pairings))
      .catch(() => undefined);
  }, []);
  useEffect(() => loadPairings(), [loadPairings]);

  // Paint the two used keys to reflect broadcast + mute state.
  const paintDeck = useCallback(
    async (d: Deck) => {
      try {
        const [pr, pg, pb] = live ? KEY_COLORS.stop : KEY_COLORS.goLive;
        await d.fillKeyColor(KEY_PRIMARY, pr, pg, pb);
        const [mr, mg, mb] = micMuted ? KEY_COLORS.micMuted : KEY_COLORS.micOn;
        await d.fillKeyColor(KEY_MUTE, mr, mg, mb);
        if (mountedRef.current) setDeckError(null);
      } catch (err) {
        console.error("[streamdeck] paint failed", err);
        if (mountedRef.current) setDeckError(describeDeckError(err));
      }
    },
    [live, micMuted],
  );

  // Repaint whenever the connected deck or the state it mirrors changes.
  useEffect(() => {
    if (deck) void paintDeck(deck);
  }, [deck, paintDeck]);

  const connectDeck = useCallback(async () => {
    setDeckError(null);
    try {
      const d = await openStreamDeck();
      if (!d) return; // User cancelled the chooser — do nothing.
      if (!mountedRef.current) {
        void d.close();
        return;
      }
      d.on("down", (control: DeckControl) => {
        if (control.type !== "button") return;
        if (control.index === KEY_PRIMARY) {
          if (stateRef.current.live) void actionRef.current.stop();
          else void actionRef.current.goLive();
        } else if (control.index === KEY_MUTE) {
          actionRef.current.toggleMute();
        }
      });
      d.on("error", (err: unknown) => {
        console.error("[streamdeck] device error", err);
        if (mountedRef.current) setDeckError(describeDeckError(err));
      });
      deckRef.current = d;
      setDeck(d);
    } catch (err: unknown) {
      // A cancelled chooser rejects with NotFoundError — treat as a no-op.
      if (err instanceof DOMException && err.name === "NotFoundError") return;
      console.error("[streamdeck] connect failed", err);
      if (mountedRef.current) setDeckError(describeDeckError(err));
    }
  }, []);

  const disconnectDeck = useCallback(() => {
    const d = deckRef.current;
    deckRef.current = null;
    setDeck(null);
    setDeckError(null);
    if (d) void closeDeck(d);
  }, []);

  // Clear keys + close the device on unmount so we never leak the handle.
  useEffect(
    () => () => {
      const d = deckRef.current;
      deckRef.current = null;
      if (d) void closeDeck(d);
    },
    [],
  );

  // Where the legend stands in the director's program (ADR 0022). Match on the
  // STABLE key (username), not the per-join identity, so "ON PROGRAM" survives a
  // reconnect that mints a fresh identity (ADR 0042).
  const adBreak = live && program.overlay?.kind === "ad";
  // The Legend is "on program" when their stable key fills ANY active head slot — the
  // dominant slot A in the *-large layouts, or any slot in an equal/board layout
  // (ADR 0212), not only slot A.
  const selfProgKey = identity ? selfSourceKey(identity) : undefined;
  const onProgram =
    live && !adBreak && !!selfProgKey && layoutSlots(program.layout).some((s) => slotSource(program, s) === selfProgKey);
  const onStandby = live && !adBreak && !onProgram;

  // Run the Stream Deck plugin bridge whenever the panel is open and the
  // operator has a paired deck (or is mid-pairing): publish state + long-poll
  // for key presses (ADR 0024). Cheap no-op for the majority with no deck.
  const deckLinked = deckPairings.length > 0 || pairingStarted;
  useDeckBridge(
    !!broadcast && deckLinked,
    {
      broadcastId: broadcast?.id,
      role: "legend",
      live,
      micMuted,
      onProgram,
      onAir: onProgram,
      standby: !!program.videoPaused,
      boardShown: program.overlay?.kind === "whiteboard",
      pendingQuestions: questionCue.pending,
    },
    handleDeckAction,
  );

  const startPairing = async () => {
    try {
      const { code } = await deckPair();
      if (!mountedRef.current) return;
      setPairCode(code);
      setPairingStarted(true);
      // The plugin claims the code out-of-band; refresh the list shortly after.
      setTimeout(loadPairings, 8000);
    } catch (err: unknown) {
      if (mountedRef.current) setError(msg(err));
    }
  };
  const revokeDeck = async (token: string) => {
    await deckRevoke(token).catch(() => undefined);
    loadPairings();
  };
  const airTag = adBreak
    ? { label: "AD BREAK · OFF AIR", color: SC.yellow }
    : onProgram
      ? { label: "ON PROGRAM", color: SC.green }
      : onStandby
        ? { label: "STANDBY · OFF PROGRAM", color: SC.dim }
        : null;

  // ── studioChrome styles (ADR 0087) — mirror the Director console's local style
  //    vocabulary so both operator consoles read as one system. ──
  const mono10: CSSProperties = { fontFamily: SC.mono, fontSize: 10, letterSpacing: "0.06em", color: SC.dim };
  const monoVal: CSSProperties = { fontFamily: SC.mono, fontSize: 11, letterSpacing: "0.04em", color: SC.dim };
  const fieldLabel: CSSProperties = { display: "block", fontFamily: SC.mono, fontSize: 10, letterSpacing: "0.05em", color: SC.dim, marginBottom: 6 };
  const selStyle: CSSProperties = { width: "100%", padding: "8px 10px", borderRadius: 9, border: `1px solid ${SC.hair2}`, background: SC.meterBg, color: SC.text, fontFamily: SC.sans, fontSize: 13 };
  const mkBtn = (bg: string, fg: string, bd: string): CSSProperties => ({ padding: "8px 14px", borderRadius: 10, border: `1px solid ${bd}`, background: bg, color: fg, fontFamily: SC.sans, fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" });
  const greenBtn = mkBtn(SC.green, "#fff", SC.green);
  const redBtn = mkBtn(SC.red, "#fff", "rgba(255,69,58,0.6)");
  const ghostBtn = mkBtn(SC.chipBg, SC.text, SC.hair3);
  const activeBtn = mkBtn(SC.redTint, SC.redSoft, SC.redEdge);
  const alertStyleSC: CSSProperties = { margin: 0, padding: "10px 12px", borderRadius: 10, border: `1px solid ${SC.redEdge}`, background: SC.redTint, color: SC.redSoft, fontSize: 13 };

  // ── header (mirrors the Director console top bar, ADR 0087): brand · LIVE/air
  //    status · mic-muted · lifecycle (Go live / Stop / End / Mute mic). ──
  const header = (
    <header style={{ display: "flex", alignItems: "center", gap: 12, padding: "0 12px", minHeight: 40, flexWrap: "wrap", borderBottom: `1px solid ${SC.divider}`, background: SC.panel, flex: "none" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 22, height: 22, borderRadius: 9, background: SC.red, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff" }} />
        </div>
        <div style={{ fontWeight: 600, fontSize: 14, letterSpacing: "0.06em" }}>LEGEND</div>
        <div style={{ fontFamily: SC.mono, fontSize: 10, letterSpacing: "0.06em", color: SC.dim, paddingTop: 1 }}>GO LIVE</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "5px 12px 5px 10px", borderRadius: 999, background: live ? SC.redTint2 : "rgba(255,255,255,0.06)", border: `1px solid ${live ? SC.redEdge : SC.hair2}` }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: live ? SC.red : SC.dim, animation: live ? "tally 1.6s ease-in-out infinite" : undefined }} />
        <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", color: live ? SC.redSoft : SC.dim }}>{live ? "LIVE" : program.ended ? "ENDED" : "OFF AIR"}</div>
        <div style={{ fontFamily: SC.mono, fontSize: 12, color: "#fff" }}>{formatClock(elapsedMs)}</div>
      </div>
      <div style={{ display: "block", flex: "0 1 auto", minWidth: 40, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: SC.mono, fontSize: 11, lineHeight: 1.4, color: SC.faint }}>
        {conn === "reconnecting" ? "RECONNECTING" : conn === "reconnected" || conn === "live" ? "LINK OK" : live ? "LINK LOST" : "READY"}
      </div>
      {airTag && (
        <span role="status" style={{ fontFamily: SC.mono, fontSize: 11, letterSpacing: "0.06em", color: airTag.color }}>{airTag.label}</span>
      )}
      {micMuted && <span style={{ fontFamily: SC.mono, fontSize: 11, letterSpacing: "0.06em", color: SC.redSoft }}>MIC MUTED</span>}
      {live && rehearsal && (
        <span role="status" aria-label="Rehearsal — private, not public" style={{ fontFamily: SC.mono, fontSize: 11, letterSpacing: "0.06em", color: SC.yellow, border: `1px solid ${SC.yellow}`, borderRadius: 6, padding: "2px 6px" }}>REHEARSAL · NOT PUBLIC</span>
      )}
      <div style={{ flex: 1 }} />
      {live ? (
        <>
          <button type="button" aria-label="Stop broadcast" onClick={() => void stopShow()} style={ghostBtn}>Stop — standby</button>
          {!program.ended && (
            <button type="button" aria-label="End broadcast" disabled={ending} onClick={() => void endShow()} style={{ ...redBtn, opacity: ending ? 0.6 : 1 }}>{ending ? "Ending…" : "End broadcast"}</button>
          )}
          {/* Fixed width so "Mute mic" ↔ "Unmute mic" don't change the button's
              width and reflow the wrapped header — which was bumping Exit onto
              another row on every toggle (LSC-173). */}
          <button type="button" aria-label={micMuted ? "Unmute microphone" : "Mute microphone"} aria-pressed={micMuted} onClick={toggleMute} style={{ ...(micMuted ? activeBtn : ghostBtn), minWidth: 96, textAlign: "center" }}>{micMuted ? "Unmute mic" : "Mute mic"}</button>
        </>
      ) : (
        <>
          {/* Private rehearsal (ADR 0244): dry-run in the real Cave without going public. */}
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: SC.mono, fontSize: 11, letterSpacing: "0.04em", color: rehearsal ? SC.yellow : SC.dim, cursor: "pointer", whiteSpace: "nowrap" }}>
            <input type="checkbox" aria-label="Rehearsal (private)" checked={rehearsal} onChange={(e) => setRehearsal(e.target.checked)} />
            Rehearsal
          </label>
          <button type="button" aria-label="Go live" disabled={!broadcast || connecting || cameras.length === 0} onClick={goLive} style={{ ...greenBtn, opacity: !broadcast || connecting || cameras.length === 0 ? 0.5 : 1 }}>{connecting ? "Connecting…" : program.ended ? "Go live again" : "Go live"}</button>
        </>
      )}
      {onClose && (
        <button type="button" aria-label="Exit" onClick={exitShow} style={{ padding: "8px 16px", borderRadius: 11, border: `1px solid ${SC.redEdge}`, background: SC.redTint, color: SC.redSoft, fontFamily: SC.sans, fontSize: 12, fontWeight: 600, letterSpacing: "0.06em", cursor: "pointer", flex: "none", whiteSpace: "nowrap" }}>Exit</button>
      )}
    </header>
  );

  // ── nav (workspace tabs) — mirrors the Director console (ADR 0087) with the
  //    Legend's reduced IA: one camera, no program/preview bus. ──
  const nav = (
    <nav aria-label="Workspace" style={{ display: "flex", alignItems: "center", gap: 2, padding: "0 10px", height: 32, borderBottom: `1px solid ${SC.divider}`, background: "rgba(20,20,22,0.82)", flex: "none", overflowX: "auto" }}>
      {visibleWorkspaces(workspaces).map((t) => {
        // Badge the Questions tab when the Director has sent/advanced a question and
        // the Legend is working on ANOTHER tab (LSC-245), so they can tell at a glance
        // a question is waiting without keeping Questions open. Clears when they open
        // Questions (view) or ack it (the cue goes null). Reuses the same cue state as
        // the Questions panel — no new poll.
        const waiting = t.id === "questions" && tab !== "questions" && questionCue.cue != null;
        const waitingCount = Math.max(1, questionCue.pending);
        return (
          <button key={t.id} type="button" aria-pressed={tab === t.id} onClick={() => setTab(t.id)} title={waiting ? `${waitingCount} question${waitingCount === 1 ? "" : "s"} waiting from the director` : undefined} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 15px", borderRadius: 999, border: `1px solid ${waiting ? SC.blueEdge : tab === t.id ? SC.blueEdge : "transparent"}`, background: tab === t.id ? SC.blueTint : waiting ? SC.blueTint : "transparent", color: tab === t.id ? SC.blueHi : waiting ? SC.blueHi : SC.dim, fontFamily: SC.sans, fontSize: 13, fontWeight: 450, cursor: "pointer", whiteSpace: "nowrap" }}>
            {t.name}
            {waiting && (
              <span aria-label={`${waitingCount} question${waitingCount === 1 ? "" : "s"} waiting`} style={{ minWidth: 16, height: 16, padding: "0 5px", borderRadius: 999, background: SC.blue, color: "#fff", fontFamily: SC.mono, fontSize: 10, fontWeight: 600, display: "inline-flex", alignItems: "center", justifyContent: "center", animation: "tally 1.6s ease-in-out infinite" }}>
                {waitingCount}
              </span>
            )}
          </button>
        );
      })}
      <div style={{ flex: 1 }} />
      <button type="button" aria-label={editMode ? "Done editing layout" : "Edit layout"} aria-pressed={editMode} onClick={() => setEditMode((v) => !v)} style={{ padding: "5px 10px", borderRadius: 8, border: `1px solid ${editMode ? SC.blueEdge : SC.hair3}`, background: editMode ? SC.blueTint : "transparent", color: editMode ? SC.blueHi : SC.dim, fontFamily: SC.mono, fontSize: 11, cursor: "pointer", whiteSpace: "nowrap", flex: "none" }}>{editMode ? "✓ Done" : "✎ Edit"}</button>
      <button type="button" aria-label="Edit tabs" aria-expanded={tabsEditorOpen} onClick={() => setTabsEditorOpen((v) => !v)} style={{ padding: "5px 10px", borderRadius: 8, border: `1px solid ${tabsEditorOpen ? SC.blueEdge : SC.hair3}`, background: tabsEditorOpen ? SC.blueTint : "transparent", color: tabsEditorOpen ? SC.blueHi : SC.dim, fontFamily: SC.mono, fontSize: 11, cursor: "pointer", whiteSpace: "nowrap", flex: "none" }}>⚙ Tabs</button>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: SC.mono, fontSize: 10, letterSpacing: "0.06em", color: SC.dim, padding: "0 4px 0 8px", whiteSpace: "nowrap" }}>
        <span>SHOW</span>
        <span style={{ color: "#e5e5ea", letterSpacing: "0.04em", fontFamily: SC.sans, fontSize: 12 }}>{event.title}</span>
      </div>
    </nav>
  );

  // ── monitor row (mirrors the Director's dual-bus row, ADR 0087): the single
  //    self-monitor (one source, no PREVIEW|TAKE|PROGRAM bus) + a status column. ──
  // On the BOARD tab the telestrator is the work surface, so the spine shrinks to a
  // small self-thumbnail and drops the status column (elapsed/link live in the header
  // already) — giving the board materially more vertical space on small laptops (LSC-405).
  const boardFocus = tab === "board";
  const monitorRow = (
    <div style={{ display: "flex", gap: 10, alignItems: "stretch", flex: "none", minWidth: 0, flexWrap: "wrap" }}>
      <div aria-label="Self monitor" style={{ flex: boardFocus ? "0 0 200px" : "1 1 340px", minWidth: 0, maxWidth: boardFocus ? 200 : 560 }}>
        <SCMonitor bus={onProgram ? "program" : "preview"} name="SELF" slug={live ? (identity ? identity.split("#")[0] : "live") : "preview"}>
          <video
            ref={videoRef}
            muted
            autoPlay
            playsInline
            aria-label="Camera preview"
            style={eyedropper ? { ...monitorVideoStyle, cursor: "crosshair" } : monitorVideoStyle}
            onClick={eyedropper ? (e) => sampleKeyColor(e.currentTarget, e.clientX, e.clientY) : undefined}
          />
        </SCMonitor>
      </div>
      {!boardFocus && (
        <div style={{ flex: "1 1 200px", minWidth: 200, background: SC.panel, border: `1px solid ${SC.hair}`, borderRadius: 12, padding: 12, display: "flex", flexDirection: "column", gap: 9, justifyContent: "center" }}>
          {[
            { k: "BUS", v: onProgram ? "program" : "preview" },
            { k: "SOURCE", v: identity ? identity.split("#")[0] : "camera" },
            { k: "ELAPSED", v: formatClock(elapsedMs) },
            { k: "LINK", v: conn === "reconnecting" ? "reconnecting" : conn === "reconnected" || conn === "live" ? "connected" : live ? "lost" : "ready" },
          ].map((r) => (
            <div key={r.k} style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
              <span style={mono10}>{r.k}</span>
              <span style={{ fontFamily: SC.mono, fontSize: 12, color: SC.text }}>{r.v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── Console tab: source + camera/mic (pre-live) or session + program hold (live) ──
  const consoleTab = (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(220px,320px)", gap: 10, alignItems: "start" }}>
      <section style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
        {live && adBreak && (
          <div role="status" aria-label="Ad break" style={{ padding: "10px 14px", borderRadius: 11, border: `1px solid ${SC.yellow}`, background: "rgba(255,214,10,0.12)", color: SC.text, fontSize: 13 }}>
            <strong style={{ color: SC.yellow }}>Ad break — you're off air.</strong> The director is running{" "}
            {program.overlay?.adName ? `“${program.overlay.adName}”` : "a break"}. Take a breather; you'll be back on
            program when they cut back to you.
          </div>
        )}
        {/* Camera + mic — selectable pre-live AND in-session (LSC-613). A pick while
            live swaps the active camera in place (raw via switchActiveDevice, or the
            chroma/ML compositor's input via setSource), keeping the same room, audio,
            Program assignment and recording — no new TAKE, no event restart. PTZ stays
            pre-live (it drives the raw preview track). */}
        <SCPanel label="CAMERA & MIC">
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <label style={{ flex: "1 1 200px" }}>
                <span style={fieldLabel}>Camera</span>
                <select aria-label="Camera" value={cameraId} disabled={switchingDevice} onChange={(e) => onPickCamera(e.target.value)} style={selStyle}>
                  {cameras.length === 0 && <option value="">No camera</option>}
                  {cameras.map((c, i) => <option key={c.deviceId} value={c.deviceId}>{c.label || `Camera ${i + 1}`}</option>)}
                </select>
              </label>
              <label style={{ flex: "1 1 200px" }}>
                <span style={fieldLabel}>Microphone</span>
                <select aria-label="Microphone" value={micId} disabled={switchingDevice} onChange={(e) => onPickMic(e.target.value)} style={selStyle}>
                  {mics.length === 0 && <option value="">No microphone</option>}
                  {mics.map((m, i) => <option key={m.deviceId} value={m.deviceId}>{m.label || `Microphone ${i + 1}`}</option>)}
                </select>
              </label>
            </div>
            {!live && ptzKeys.length > 0 && (
              <div style={{ display: "grid", gap: 8 }} aria-label="Camera controls">
                {ptzKeys.map((k) => {
                  const range = ptzRanges[k];
                  if (!range) return null;
                  return (
                    <label key={k} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ ...mono10, minWidth: 44 }}>{PTZ_LABEL[k]}</span>
                      <input
                        type="range"
                        aria-label={PTZ_LABEL[k]}
                        min={range.min}
                        max={range.max}
                        step={range.step ?? 1}
                        value={ptzValues[k] ?? range.min}
                        onChange={(e) => onPtzChange(k, Number(e.target.value))}
                        style={{ flex: 1, accentColor: SC.blue }}
                      />
                    </label>
                  );
                })}
              </div>
            )}
            {switchingDevice && (
              <span role="status" style={{ ...mono10, color: SC.blue }}>Switching camera…</span>
            )}
          </SCPanel>
        {live && (
          <SCPanel label="SESSION">
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "baseline" }}>
              {roomName && <span style={monoVal}>ROOM {roomName}</span>}
              {identity && <span style={monoVal}>AS {identity.split("#")[0]}</span>}
              {program.ended && <span style={{ ...monoVal, color: SC.dim }}>Broadcast ended.</span>}
            </div>
          </SCPanel>
        )}
      </section>
      <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {/* Production hold (ADR 0055) — mute program audio / pause program video for
            fans (pausing pops the cave's rotating hold playlist). Authoritative at
            the SFU + relayed. Only meaningful while live; the Legend's own camera
            preview above keeps running. Pause offers a timed auto-resume. */}
        {live ? (
          <SCPanel label="PROGRAM HOLD">
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <button type="button" aria-label={program.audioMuted ? "Unmute audio" : "Mute audio"} onClick={toggleAudioMuted} style={program.audioMuted ? activeBtn : ghostBtn}>
                {program.audioMuted ? "Unmute" : "Mute audio"}
              </button>
              {!program.videoPaused && (
                <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.05em", color: SC.dim }}>AUTO-RESUME</span>
                  <select aria-label="Auto-resume after" value={pauseSecondsStr} onChange={(e) => setPauseSecondsStr(e.target.value)} style={{ ...selStyle, width: "auto" }}>
                    {HOLD_DURATIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </label>
              )}
              <button type="button" aria-label={program.videoPaused ? "Resume video" : "Pause video"} onClick={toggleVideoPaused} style={program.videoPaused ? activeBtn : ghostBtn}>
                {program.videoPaused ? "Resume" : "Pause video"}
              </button>
              {(program.audioMuted || program.videoPaused) && (
                <span role="status" style={{ ...monoVal, color: SC.yellow }}>
                  FANS ON HOLD{program.audioMuted ? " · AUDIO MUTED" : ""}
                  {program.videoPaused ? " · VIDEO PAUSED" : ""}
                </span>
              )}
            </div>
          </SCPanel>
        ) : (
          <SCPanel label="READY">
            <span style={{ fontSize: 13, color: SC.dim }}>Pick your camera and mic, then hit Go live in the top bar.</span>
          </SCPanel>
        )}
      </section>
    </div>
  );

  // ── Background tab: the SHARED chroma/ML control panel (identical to the
  //    Director's), wrapped in the studio rack (ADR 0070/0071). ──
  const backgroundTab = (
    <SCPanel label="BACKGROUND">
      <ChromaControls
        chroma={chroma}
        backgrounds={backgrounds}
        bgBusy={bgBusy}
        statusLabel={chroma?.by === "director" ? "Director override" : undefined}
        onSelectBackground={selectBackground}
        onToggleEnabled={toggleUseBackground}
        onSetMode={(m) => setLegendChroma({ mode: m })}
        onSet={setLegendChroma}
        onTune={tuneChroma}
        onUpload={onUploadBackgroundFile}
        onRemove={onRemoveBackground}
        eyedropper={{ armed: eyedropper, onToggle: () => setEyedropper((v) => !v) }}
        warning={chromaWarn ?? gpuAdvisory}
      />
    </SCPanel>
  );

  // ── Board tab: shared telestration (ADR 0045) or the publisher's own dueling
  //    board (ADR 0054); a hint when the director hasn't shared one. ──
  const boardTab = (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* The telestration surface comes FIRST so it gets the top of the viewport, not
          buried under the device block (LSC-405). The Whiteboard-from-Device block is
          a collapsible panel BELOW — the QR only takes space when the operator opens it. */}
      {telestrating && wb && (
        <SCPanel label="TELESTRATION">
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button type="button" aria-label="Clear whiteboard" onClick={clearBoard} style={{ ...ghostBtn, padding: "6px 12px", fontSize: 11 }}>Clear</button>
          </div>
          <div style={boardCanvasWrap}>
            <WhiteboardCanvas
              strokes={wb.strokes ?? []}
              board={wb.board}
              boardImageUrl={resolveBoardImageUrl(wb.board, boardSlates)}
              editable
              onChange={drawStrokes}
              color="#c9a227"
              ariaLabel="Telestration editor"
            />
          </div>
        </SCPanel>
      )}
      {duelling && myDuelSlot && (
        <SCPanel label={`YOUR BOARD · SLOT ${myDuelSlot.toUpperCase()}`}>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button type="button" aria-label="Clear your board" onClick={clearDuelBoard} style={{ ...ghostBtn, padding: "6px 12px", fontSize: 11 }}>Clear</button>
          </div>
          <div style={boardCanvasWrap}>
            <WhiteboardCanvas
              strokes={duelBoard?.strokes ?? []}
              board={duelBoard?.board}
              boardImageUrl={resolveBoardImageUrl(duelBoard?.board, boardSlates)}
              editable
              onChange={drawDuelStrokes}
              color="#c9a227"
              ariaLabel="Your board editor"
            />
          </div>
        </SCPanel>
      )}
      {!(telestrating && wb) && !(duelling && myDuelSlot) && (
        <SCPanel label="BOARD">
          <span style={{ fontSize: 13, color: SC.dim }}>No board yet — the director shares a telestration or dueling board while you're live, and it opens here for you to draw on.</span>
        </SCPanel>
      )}
      <SCPanel label="WHITEBOARD FROM DEVICE" collapsible defaultCollapsed={!deviceUrl}>
        <span style={{ fontSize: 13, color: SC.dim }}>
          Draw the board from an iPad, tablet, or phone with a stylus or finger — a secondary input; your laptop board above keeps working.
        </span>
        {!deviceUrl ? (
          <button type="button" aria-label="Whiteboard from Device" disabled={!broadcast || deviceBusy}
            onClick={openWhiteboardDevice}
            style={{ alignSelf: "flex-start", padding: "10px 16px", borderRadius: 11, border: `1px solid ${SC.blueEdge}`, background: SC.blueTint, color: SC.blueHi, fontFamily: SC.sans, fontSize: 13, fontWeight: 500, cursor: "pointer", opacity: !broadcast || deviceBusy ? 0.5 : 1 }}>
            {deviceBusy ? "Generating…" : "Whiteboard from Device"}
          </button>
        ) : (
          <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            {deviceQr && <img src={deviceQr} alt="Whiteboard device QR code" width={176} height={176} style={{ borderRadius: 10, background: "#fff", padding: 8, flex: "none" }} />}
            <div style={{ display: "grid", gap: 8, minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12, color: SC.text2 }}>Scan with a touch device to draw on the board.</div>
              <div style={{ fontFamily: SC.mono, fontSize: 11, color: SC.dim, wordBreak: "break-all" }}>{deviceUrl}</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" aria-label="Copy device link" onClick={() => void navigator.clipboard?.writeText(deviceUrl).catch(() => undefined)} style={ghostBtn}>Copy link</button>
                <button type="button" aria-label="New device code" disabled={deviceBusy} onClick={openWhiteboardDevice} style={ghostBtn}>New code</button>
              </div>
            </div>
          </div>
        )}
      </SCPanel>
    </div>
  );

  // ── Questions tab: the advanceable approved-question cue (ADR 0046) + the
  //    Ask-the-Legend toggle (ADR 0077) under Advanced settings. ──
  const questionsTab = (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {live ? (
        questionCue.cue ? (
          <section aria-label="Fan question" style={{ display: "grid", gap: 5, padding: "12px 14px", borderRadius: 11, border: `1px solid ${SC.blueEdge}`, background: SC.panel2, color: SC.text }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ ...mono10, color: SC.blueHi }}>FAN QUESTION</span>
              {questionCue.pending > 0 && (
                <span aria-label={`${questionCue.pending} in queue`} style={{ fontFamily: SC.mono, fontSize: 10, padding: "1px 7px", borderRadius: 999, border: `1px solid ${SC.blueEdge}`, color: SC.blueHi }}>
                  {questionCue.pending} in queue
                </span>
              )}
            </div>
            <strong style={{ fontFamily: SC.sans, fontSize: 12 }}>{questionCue.cue.fan}</strong>
            <span style={{ fontFamily: SC.sans, fontSize: 13, lineHeight: 1.45 }}>{questionCue.cue.text}</span>
            <div style={{ marginTop: 2 }}>
              <button type="button" onClick={() => { questionCue.ack().catch(() => undefined); }} style={mkBtn(SC.blue, "#fff", SC.blue)}>
                {questionCue.pending > 1 ? "Done — next question" : "Done"}
              </button>
            </div>
          </section>
        ) : (
          <p aria-label="Fan question queue" style={{ margin: 0, fontSize: 11, color: SC.dim }}>
            No fan questions in the queue.
          </p>
        )
      ) : (
        <SCPanel label="FAN QUESTIONS">
          <span style={{ fontSize: 13, color: SC.dim }}>Approved fan questions from the director appear here once you're live.</span>
        </SCPanel>
      )}
      <SCPanel label="SETTINGS">
        <details>
          <summary style={{ ...mono10, cursor: "pointer", color: SC.text }}>Advanced settings</summary>
          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "10px 0 2px" }}>
            <span style={mono10}>ASK THE LEGEND</span>
            <button
              type="button"
              aria-label={program.askDisabled ? "Enable Ask the Legend" : "Disable Ask the Legend"}
              aria-pressed={!program.askDisabled}
              onClick={() => void relayProgram({ ...program, askDisabled: !program.askDisabled })}
              style={ghostBtn}
            >
              {program.askDisabled ? "Off" : "On"}
            </button>
            <span style={{ fontSize: 12, color: SC.dim }}>
              {program.askDisabled ? "Fans can't submit questions." : "Fans can submit questions."}
            </span>
          </div>
        </details>
      </SCPanel>
    </div>
  );

  // ── Deck tab: Stream Deck pairing + the direct-connect fallback (ADR 0024). ──
  const deckTab = (
    <SCPanel label="STREAM DECK">
      <div style={{ display: "grid", gap: 8 }}>
        <span style={monoVal}>Runs inside the Elgato app — no need to quit it.</span>

        {deckPairings.length > 0 ? (
          <div style={{ display: "grid", gap: 6 }}>
            <span style={{ ...monoVal, color: SC.green }}>
              ✓ PAIRED · KEY = {live ? "STOP" : "GO LIVE"} · KEY = {micMuted ? "UNMUTE" : "MUTE"}
            </span>
            {deckPairings.map((p) => (
              <div key={p.token} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={monoVal}>{p.label} · {p.token}</span>
                <button type="button" aria-label={`Unpair ${p.label}`} onClick={() => void revokeDeck(p.ref)} style={{ ...ghostBtn, padding: "5px 10px", fontSize: 11 }}>Unpair</button>
              </div>
            ))}
            <div>
              <button type="button" aria-label="Pair another Stream Deck" onClick={() => void startPairing()} style={{ ...ghostBtn, padding: "6px 12px", fontSize: 11 }}>Pair another</button>
            </div>
          </div>
        ) : (
          <div>
            <button type="button" aria-label="Pair a Stream Deck" onClick={() => void startPairing()} style={{ ...mkBtn(SC.blue, "#fff", SC.blue), padding: "6px 12px", fontSize: 11 }}>Pair a Stream Deck</button>
          </div>
        )}

        {pairCode && (
          <div role="status" style={{ padding: "10px 14px", borderRadius: 11, border: `1px solid ${SC.hair3}`, background: SC.blueTint, display: "grid", gap: 6 }}>
            <span style={mono10}>PAIRING CODE</span>
            <strong style={{ fontFamily: SC.mono, fontSize: 22, letterSpacing: "0.28em", color: SC.blueHi }}>{pairCode}</strong>
            <ol style={{ ...monoVal, margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
              <li>
                <a href={PLUGIN_DOWNLOAD_PATH} download style={{ color: SC.blueHi }}>Install the plugin</a>{" "}
                (once per computer)
              </li>
              <li>Add the “Legend Sports Cave” action to a Stream Deck key</li>
              <li>Enter this code in the action’s settings — it expires in 5 min</li>
            </ol>
          </div>
        )}

        <details>
          <summary style={{ ...monoVal, cursor: "pointer" }}>No Elgato app? Connect directly (advanced)</summary>
          <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
            {!hidOk ? (
              <span style={monoVal}>Stream Deck needs Chrome/Edge</span>
            ) : deck ? (
              <>
                <span style={monoVal}>{live ? "KEY 0 = STOP" : "KEY 0 = GO LIVE"} · KEY 1 = {micMuted ? "UNMUTE" : "MUTE"}</span>
                <div style={{ display: "flex", gap: 10 }}>
                  <button type="button" aria-label="Disconnect Stream Deck" onClick={disconnectDeck} style={{ ...ghostBtn, padding: "6px 12px", fontSize: 11 }}>Disconnect</button>
                </div>
              </>
            ) : (
              <div style={{ display: "flex", gap: 10 }}>
                <button type="button" aria-label="Connect Stream Deck" onClick={() => void connectDeck()} style={{ ...ghostBtn, padding: "6px 12px", fontSize: 11 }}>Connect Stream Deck (direct)</button>
              </div>
            )}
            {deckError && (
              <span role="alert" style={{ ...monoVal, color: SC.redSoft, maxWidth: 360, lineHeight: 1.4 }}>{deckError}</span>
            )}
          </div>
        </details>
      </div>
    </SCPanel>
  );

  // Full-screen-only operator console (ADR 0074) reproduced in the RelayStudio
  // chrome (ADR 0087), structurally MIRRORING the Director console: a fixed dialog
  // with the studio header + pill-tab nav over the self-monitor row + tab content.
  // The Legend has one camera source and no preview/program bus, so the dual-bus
  // TAKE column is dropped and lifecycle lives in the header.
  // ── Legend widget registry + active-workspace canvas prep (ADR 0126) ─────────────
  const prodChatPanel = (
    <ProductionChatPanel messages={prodChat.messages} onSend={prodChat.send} unread={prodChat.unread} onRead={prodChat.markRead} peerLabel="Director" />
  );
  const widgetNodes: Record<string, ReactNode> = {
    console: consoleTab, background: backgroundTab, board: boardTab, questions: questionsTab, deck: deckTab, prodChat: prodChatPanel,
  };
  const widgetDefFor = (id: string): WidgetDef | null => {
    const m = LEGEND_WIDGET_META[id];
    return m ? { id, title: m.title, node: widgetNodes[id], minW: m.minW, minH: m.minH } : null;
  };
  const activeWs = workspaces.find((w) => w.id === tab) ?? workspaces.find((w) => w.id === HOME_WORKSPACE_ID) ?? workspaces[0];
  const placedWidgets = (activeWs?.widgets ?? []).map(widgetDefFor).filter((x): x is WidgetDef => x !== null);
  const widgetLocation: Record<string, string> = {};
  for (const w of workspaces) for (const id of w.widgets ?? []) widgetLocation[id] = w.name;
  const onActive = new Set(activeWs?.widgets ?? []);
  const toolboxWidgets = LEGEND_WIDGET_IDS
    .filter((id) => !onActive.has(id))
    .map((id): WidgetDef | null => { const d = widgetDefFor(id); return d ? { ...d, location: widgetLocation[id] } : null; })
    .filter((x): x is WidgetDef => x !== null);
  const updateActiveLayout = (l: WidgetLayout[]) => {
    if (!activeWs) return;
    setWorkspaces((ws) => {
      const cur = ws.find((w) => w.id === activeWs.id)?.layout ?? [];
      return layoutEqual(cur, l) ? ws : setWorkspaceLayout(ws, activeWs.id, l);
    });
  };
  const addWidgetToActive = (id: string) => {
    if (!activeWs) return;
    const m = LEGEND_WIDGET_META[id];
    setWorkspaces((ws) => placeWidget(ws, id, activeWs.id, { w: m.w, h: m.h, minW: m.minW, minH: m.minH }));
  };
  const removeWidgetFromActive = (id: string) => setWorkspaces((ws) => removeWidget(ws, id));
  const createWorkspace = () => {
    const nums = workspaces.filter((w) => !w.builtin).map((w) => Number(w.id.replace(/^ws-/, ""))).filter((n) => Number.isFinite(n));
    const n = (nums.length ? Math.max(...nums) : 0) + 1;
    const id = `ws-${n}`;
    setWorkspaces((ws) => [...ws, { id, name: `Workspace ${n}`, builtin: false, hidden: false, widgets: [], layout: [] }]);
    setTab(id);
    setEditMode(true);
    setTabsEditorOpen(false);
  };
  const resetWorkspaces = () => {
    setWorkspaces(validateWidgetPlacement(seedWidgetDefaults(reconcileWorkspaces(undefined, LEGEND_BUILTIN_WORKSPACES), LEGEND_WIDGET_DEFAULTS), new Set(LEGEND_WIDGET_IDS)));
    setTab(HOME_WORKSPACE_ID);
    setEditMode(false);
    setTabsEditorOpen(false);
  };

  return (
    <div role="dialog" aria-modal aria-label={`Go live · ${event.title}`} style={{ position: "fixed", inset: 0, zIndex: 200, height: "calc(100dvh / var(--ui-zoom, 1))", overflow: "hidden", display: "flex", flexDirection: "column", color: SC.text, background: SC.bg, fontFamily: SC.sans }}>
      {/* Director talkback (ADR 0213): the private return-audio plays here (hidden),
          with a floating indicator so the Legend knows the Director is in their ear —
          never part of the fan/program mix. */}
      <audio ref={talkbackAudioRef} autoPlay aria-hidden />
      {/* Talent conversation audio (LSC-530): every connected guest's mic plays here
          (hidden), independent of Program — so the Legend and guests converse. */}
      <div ref={talentAudioRef} aria-hidden style={{ display: "none" }} />
      {/* Director mic → On Air (ADR 0247): the Director's public on-air voice plays here
          (hidden) when they're On Air — the fan-mix plane, separate from private talkback. */}
      <div ref={directorMicRef} aria-hidden style={{ display: "none" }} />
      {talkbackActive && (
        <div role="status" aria-label="Director talkback active" style={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", zIndex: 300, display: "flex", alignItems: "center", gap: 7, padding: "6px 12px", borderRadius: 999, background: SC.redTint, border: `1px solid ${SC.red}`, color: SC.redSoft, fontFamily: SC.mono, fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", boxShadow: "0 2px 10px rgba(0,0,0,0.45)" }}>
          <span aria-hidden style={{ width: 8, height: 8, borderRadius: "50%", background: SC.red }} />
          DIRECTOR TALKBACK
        </div>
      )}
      {header}
      {nav}
      {tabsEditorOpen && (
        <WorkspaceTabsEditor workspaces={workspaces} onChange={setWorkspaces} onClose={() => setTabsEditorOpen(false)} onCreate={createWorkspace} onReset={resetWorkspaces} />
      )}
      <main style={{ flex: 1, display: "flex", flexDirection: "column", padding: 10, gap: 10, minHeight: 0, overflow: "hidden" }}>
        {/* Suppress the reconnect/lost banner once the broadcast is deliberately
            ended — the room teardown fires Disconnected, but that's expected, not a
            failure (the header already shows ENDED + Go live again). */}
        {!program.ended && <ConnectionBanner state={conn} onRejoin={() => void goLive()} />}
        {monitorRow}
        {/* The widget canvas scrolls VERTICALLY when it can't fit — on a short
            viewport (iPad landscape) the lower controls (camera/mic selects) used to
            fall below the fold with overflow:hidden and no scroll path (LSC-173).
            overflow-x stays hidden so nothing scrolls sideways. */}
        <div style={{ flex: 1, minHeight: 0, overflowX: "hidden", overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, paddingRight: 2 }}>
          <div style={{ flex: 1, minHeight: 0 }}>
            <WorkspaceCanvas
              placed={placedWidgets}
              toolbox={toolboxWidgets}
              layout={activeWs?.layout ?? []}
              editMode={editMode}
              onLayoutChange={updateActiveLayout}
              onAdd={addWidgetToActive}
              onRemove={removeWidgetFromActive}
            />
          </div>
          {error && <p role="alert" style={alertStyleSC}>{error}</p>}
        </div>
      </main>
    </div>
  );
}

/** Clear the used keys and release the Stream Deck; swallow any HID errors. */
async function closeDeck(d: Deck): Promise<void> {
  try {
    await d.clearKey(KEY_PRIMARY);
    await d.clearKey(KEY_MUTE);
  } catch {
    // Device may already be gone — closing is what matters.
  }
  try {
    await d.close();
  } catch {
    // Nothing more we can do; never throw from cleanup.
  }
}

function msg(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** h:mm:ss elapsed clock for the header (mirrors the Director console, ADR 0087). */
function formatClock(ms: number): string {
  const total = Number.isFinite(ms) && ms > 0 ? Math.floor(ms / 1000) : 0;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${h}:${pad(m)}:${pad(s)}`;
}

/** getUserMedia rejections carry a DOMException name we can turn into guidance. */
function deviceErr(err: unknown): string {
  const name = err instanceof DOMException ? err.name : err instanceof Error ? err.name : "";
  if (name === "NotAllowedError" || name === "SecurityError") {
    return "Camera and microphone access was denied.";
  }
  if (name === "NotFoundError" || name === "OverconstrainedError") {
    return "No camera or microphone was found.";
  }
  return msg(err);
}
